import { initial } from "lodash";
import { Flex, Icon, Popover, Text } from "orcs-design-system";
import PropTypes from "prop-types";
import React, { Fragment } from "react";
import styled from "styled-components";
import { getGroupNamesArrayFromHierarchy } from "src/allocation/util/group";
import TeamLink from "src/components/TeamLink";
import GroupTypeBadge from "src/components/GroupTypeBadge";
import * as searchUtil from "src/util/search";
import GroupPropType from "../../custom-prop-types/group";

// Plain-text stand-in for TeamLink when the breadcrumb must not navigate
// (e.g. inside a picker in a modal). Extends ORCS Text to keep token styling.
const TeamName = styled(Text)`
  white-space: nowrap;
`;

const HierarchyTooltip = ({
  className,
  selectedString,
  group,
  showFullHierarchyInBreadcrumb,
  bold,
  disableLinks = false,
  showGroupTypeBadge = false,
  groupTypes,
  tabIndex,
  children,
}) => {
  const { hierarchy } = group;

  const hierarchyTeams = hierarchy
    ? getGroupNamesArrayFromHierarchy(group.hierarchy)
    : [];
  const parentGroupIds = group.parentIds || initial(group.hierarchyIds); // get all but last if using hierarchyIds

  const breadcrumbGroupIds = showFullHierarchyInBreadcrumb
    ? group.hierarchyIds
    : parentGroupIds;

  if (!hierarchy && !showGroupTypeBadge) {
    return children;
  }
  return (
    <Popover
      direction="top"
      width="min-content"
      tabIndex={tabIndex}
      text={
        <Flex alignItems="center" flexWrap="wrap" gap="xs">
          {breadcrumbGroupIds.map((breadcrumbGroupId, index) => {
            const teamName = searchUtil.highlightSelectedString(
              hierarchyTeams[index],
              selectedString
            );
            return (
              <Fragment key={breadcrumbGroupId}>
                <Flex alignItems="center" flexWrap="nowrap">
                  {disableLinks ? (
                    <TeamName
                      as="span"
                      className={className}
                      color="bodyText"
                      fontSize="0"
                      fontWeight={bold ? "2" : "1"}
                      data-testid={`breadcrumb-name-${breadcrumbGroupId}`}
                    >
                      {teamName}
                    </TeamName>
                  ) : (
                    <TeamLink
                      className={className}
                      noWrap
                      small
                      bold={bold}
                      teamName={teamName}
                      team={{ id: breadcrumbGroupId }}
                      data-testid={`breadcrumb-link-${breadcrumbGroupId}`}
                    />
                  )}
                  <Icon
                    ml="xs"
                    icon={["fas", "chevron-right"]}
                    color="mutedText"
                    size="xs"
                  />
                </Flex>
              </Fragment>
            );
          })}
          {showGroupTypeBadge && (
            <GroupTypeBadge group={group} groupTypes={groupTypes} />
          )}
        </Flex>
      }
    >
      {children}
    </Popover>
  );
};

HierarchyTooltip.propTypes = {
  group: GroupPropType.isRequired,
  className: PropTypes.string,
  bold: PropTypes.bool,
  showFullHierarchyInBreadcrumb: PropTypes.bool,
  /** Render breadcrumb team names as plain text instead of links. */
  disableLinks: PropTypes.bool,
  showGroupTypeBadge: PropTypes.bool,
  groupTypes: PropTypes.object,
  tabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  children: PropTypes.element,
  selectedString: PropTypes.string,
};

export default HierarchyTooltip;
